/** PATCH helper for the trip-type editor: optimistic concurrency + 409 flow +
 *  optional image swap (multipart). (It mirrored the tour tabs' former `useTourPatch`.) */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as tripTypesApi from '@/api/tripTypes';
import type { TripTypeDetail, TripTypeWriteJson } from '@/api/tripTypes';
import { useSnackbar } from '@/components/Snackbar';
import { diffFieldNames } from '@/utils/recordDiff';

export function useTripTypePatch(tripType: TripTypeDetail, onSaved: () => void) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [conflict, setConflict] = useState(false);
  const [changedFields, setChangedFields] = useState<string[] | undefined>(undefined);

  const mutation = useMutation({
    mutationFn: ({ body, image }: { body: TripTypeWriteJson; image?: File | null }) => {
      const payload = { ...body, expected_updated_ts: tripType.updated_ts };
      return tripTypesApi.update(tripType.id, payload, image);
    },
    onSuccess: () => {
      setConflict(false);
      setChangedFields(undefined);
      snackbar.show('Saved.', 'success');
      qc.invalidateQueries({ queryKey: ['trip_types'] });
      onSaved();
    },
    onError: (e) => {
      if (e instanceof ConsoleApiError && e.status === 409) {
        setConflict(true);
        // Name the drifted fields: fetch a fresh copy directly (NOT via the
        // page query — the form must keep its edits) and diff it against the
        // record this editor was seeded from. Best-effort.
        tripTypesApi
          .retrieve(tripType.id)
          .then((fresh) =>
            setChangedFields(
              diffFieldNames(
                tripType as unknown as Record<string, unknown>,
                fresh as unknown as Record<string, unknown>,
              ),
            ),
          )
          .catch(() => setChangedFields(undefined));
        return;
      }
      const msg = e instanceof ConsoleApiError ? e.message : 'Save failed.';
      snackbar.show(msg, 'error');
    },
  });

  return {
    save: (body: TripTypeWriteJson, image?: File | null) => mutation.mutate({ body, image }),
    saving: mutation.isPending,
    conflict,
    /** Fields differing between the editor's base record and the server (409 only). */
    changedFields,
    dismissConflict: () => {
      setConflict(false);
      setChangedFields(undefined);
    },
    /** Field-keyed server errors from a 400 (e.g. non-unique name), if any. */
    fieldErrors:
      mutation.error instanceof ConsoleApiError && mutation.error.status === 400
        ? (mutation.error.body as Record<string, unknown>)
        : null,
  };
}
